// Permission catalog v1 — `auth:PermissionCatalog@v1` (users-roles-permissions R1).
//
// Source of truth: docs/verticals/users-roles-permissions.md §4 (88 keys, 16 groups).
// The vocabulary lives in code because every enforcement point is code: a release that
// adds a permission ships the key, its enforcement and its system-role grants together.
//
// Browser-safe (no node imports): operator-ui compiles this file from source.

import { deepFreeze } from './freeze';

/** Key format `<module>.<action>[.<qualifier>]` — lower-case, `_` allowed inside a segment. */
export const PERMISSION_KEY_PATTERN = /^[a-z][a-z_]*\.[a-z][a-z_]*(\.[a-z][a-z_]*)?$/;

/** Every permission key of catalog v1 (§4.1–§4.16), in catalog order. */
export type PermissionKey =
  // §4.1 Panel y búsqueda
  | 'dashboard.view'
  | 'passengers.search'
  // §4.2 Disrupciones
  | 'disruptions.view'
  | 'disruptions.declare'
  | 'disruptions.run'
  | 'disruptions.contain'
  | 'disruptions.contain.force'
  | 'disruptions.pnr.exclude'
  | 'disruptions.repair'
  // §4.3 Casos y PNR
  | 'cases.view'
  | 'cases.create'
  | 'cases.pnr.add'
  | 'cases.care_only'
  | 'cases.resolve'
  | 'cases.outcomes.declare'
  // §4.4 Vuelos
  | 'flights.view'
  // §4.5 Reacomodación
  | 'rebooking.generate'
  | 'rebooking.generate.force'
  | 'rebooking.select'
  | 'rebooking.override'
  | 'rebooking.override.waive_rule'
  | 'rebooking.ticket.issue'
  | 'rebooking.ticket.issue_bulk'
  | 'rebooking.external'
  | 'rebooking.lcc.search'
  | 'rebooking.lcc.purchase'
  // §4.6 Aceptación y comunicación con el pasajero
  | 'acceptance.force'
  | 'acceptance.force_all'
  | 'notifications.send'
  // §4.7 Hoteles
  | 'hotels.allocate'
  | 'hotels.book'
  | 'hotels.policy.override'
  | 'hotels.release'
  | 'hotels.stay'
  | 'hotels.rooming.export'
  // §4.8 Traslados
  | 'transport.plan'
  | 'transport.operate'
  | 'transport.vouchers'
  | 'transport.manifest.export'
  // §4.9 Asistencia y excepciones de beneficio
  | 'care.view'
  | 'care.issue'
  | 'care.issue.custom'
  | 'care.topup'
  | 'care.topup.over_cap'
  | 'care.redeem'
  | 'care.void'
  | 'care.token.reveal'
  | 'benefits.override.propose'
  | 'benefits.override.decide'
  | 'benefits.override.apply'
  // §4.10 Datos personales
  | 'pii.name.reveal'
  | 'pii.contact.reveal'
  | 'pii.document.reveal'
  | 'pii.bulk'
  | 'pii.contact.edit'
  | 'pii.special_needs'
  // §4.11 Costos, reportes y auditoría
  | 'costs.view'
  | 'reconciliation.view'
  | 'reports.view'
  | 'reports.export'
  | 'activity.view'
  | 'audit.view'
  | 'billing.view'
  // §4.12 Importación de manifiestos
  | 'manifests.import'
  // §4.13 Reservas directas
  | 'reservations.view'
  | 'reservations.purchase'
  | 'reservations.transport'
  // §4.14 Herramientas
  | 'counters.qr'
  | 'manual_review.resolve'
  | 'provider_sim.answer'
  // §4.15 Configuración
  | 'policies.view'
  | 'policies.edit'
  | 'policies.activate'
  | 'contracts.view'
  | 'contracts.edit'
  | 'formats.view'
  | 'formats.edit'
  | 'formats.approve'
  | 'providers.view'
  | 'providers.test'
  | 'providers.toggle'
  // §4.16 Usuarios y accesos
  | 'users.view'
  | 'users.invite'
  | 'users.roles.assign'
  | 'users.scope.assign'
  | 'users.suspend'
  | 'users.export'
  | 'roles.view'
  | 'roles.manage';

/** The 16 catalog groups (§4.1–§4.16), used to lay out the role editor and the matrix. */
export type PermissionGroupId =
  | 'panel'
  | 'disruptions'
  | 'cases'
  | 'flights'
  | 'rebooking'
  | 'acceptance'
  | 'hotels'
  | 'transport'
  | 'care'
  | 'pii'
  | 'reports'
  | 'manifests'
  | 'reservations'
  | 'tools'
  | 'configuration'
  | 'users';

export interface IPermissionGroupDef {
  id: PermissionGroupId;
  /** Spanish heading from §4.x. */
  label: string;
  /** i18n key: `permissions.groups.<id>`. */
  labelKey: string;
}

/**
 * `airport` ([A]): evaluated against the user's scope — the resource's airports must
 * intersect it. `tenant` ([T]): configuration; never narrowed by airport.
 */
export type PermissionScope = 'airport' | 'tenant';

/** §4 badges: 💰 money · 🔒 PII · ⚠ high-impact / irreversible. Absent ⇒ not flagged. */
export interface IPermissionFlags {
  money?: true;
  pii?: true;
  highImpact?: true;
}

export interface IPermissionDef {
  key: PermissionKey;
  group: PermissionGroupId;
  /** Spanish copy from the §4 table. */
  label: string;
  /** i18n key: `permissions.<key>`. */
  labelKey: string;
  scope: PermissionScope;
  flags: IPermissionFlags;
}

export const PERMISSION_GROUPS: readonly IPermissionGroupDef[] = deepFreeze(
  (
    [
      ['panel', 'Panel y búsqueda'],
      ['disruptions', 'Disrupciones'],
      ['cases', 'Casos y PNR'],
      ['flights', 'Vuelos'],
      ['rebooking', 'Reacomodación'],
      ['acceptance', 'Aceptación y comunicación con el pasajero'],
      ['hotels', 'Hoteles'],
      ['transport', 'Traslados'],
      ['care', 'Asistencia (comidas / tarjetas) y excepciones de beneficio'],
      ['pii', 'Datos personales'],
      ['reports', 'Costos, reportes y auditoría'],
      ['manifests', 'Importación de manifiestos'],
      ['reservations', 'Reservas directas'],
      ['tools', 'Herramientas'],
      ['configuration', 'Configuración'],
      ['users', 'Usuarios y accesos'],
    ] as const satisfies ReadonlyArray<readonly [PermissionGroupId, string]>
  ).map(([id, label]) => ({ id, label, labelKey: `permissions.groups.${id}` })),
);

// Row shorthand: [group, scope, flags, label]. `A`/`T` = [A]/[T]; flags use the §4 emoji
// so each row reads like its table line. A `Record<PermissionKey, …>` makes the compiler
// enforce that the table covers every key exactly once (missing or extra keys fail tsc).
type Flag = '💰' | '🔒' | '⚠';
type Row = readonly [PermissionGroupId, 'A' | 'T', readonly Flag[], string];

const NONE: readonly Flag[] = [];

const CATALOG: Readonly<Record<PermissionKey, Row>> = {
  // §4.1 Panel y búsqueda
  'dashboard.view': ['panel', 'A', NONE, 'Ver panel e indicadores'],
  'passengers.search': ['panel', 'A', ['🔒'], 'Buscar pasajero, PNR, ticket o vuelo'],
  // §4.2 Disrupciones
  'disruptions.view': ['disruptions', 'A', NONE, 'Ver disrupciones (tablero, detalle, progreso, "¿Por qué?")'],
  'disruptions.declare': ['disruptions', 'A', ['⚠'], 'Declarar disrupción (un vuelo/Express, varios vuelos, desde archivo)'],
  'disruptions.run': ['disruptions', 'A', NONE, 'Ejecutar el próximo paso / autopiloto (cada paso exige además su permiso)'],
  'disruptions.contain': ['disruptions', 'A', NONE, 'Confirmar contención (cierre natural)'],
  'disruptions.contain.force': ['disruptions', 'A', ['⚠'], 'Forzar contención'],
  'disruptions.pnr.exclude': ['disruptions', 'A', NONE, 'Excluir PNR del evento'],
  'disruptions.repair': ['disruptions', 'A', NONE, 'Reparar: reenviar ofertas trabadas, re-emitir tickets fallidos'],
  // §4.3 Casos y PNR
  'cases.view': ['cases', 'A', NONE, 'Ver casos, PNR, opciones, reservas y cronología'],
  'cases.create': ['cases', 'A', NONE, 'Crear caso manual'],
  'cases.pnr.add': ['cases', 'A', NONE, 'Agregar PNR a un caso'],
  'cases.care_only': ['cases', 'A', NONE, 'Convertir caso a solo asistencia'],
  'cases.resolve': ['cases', 'A', ['⚠'], 'Resolver caso (cierre administrativo)'],
  'cases.outcomes.declare': ['cases', 'A', NONE, 'Declarar resultado (alojamiento, traslado, asistencia, check-in)'],
  // §4.4 Vuelos
  'flights.view': ['flights', 'A', NONE, 'Ver vuelos, pronóstico y manifiesto'],
  // §4.5 Reacomodación
  'rebooking.generate': ['rebooking', 'A', NONE, 'Generar opciones / auto-reacomodar'],
  'rebooking.generate.force': ['rebooking', 'A', ['⚠'], 'Generar pese a política que lo impide o caso solo asistencia'],
  'rebooking.select': ['rebooking', 'A', NONE, 'Elegir, rechazar o cancelar opción'],
  'rebooking.override': ['rebooking', 'A', NONE, 'Override manual de vuelo'],
  'rebooking.override.waive_rule': ['rebooking', 'A', ['⚠'], 'Override con dispensa de regla'],
  'rebooking.ticket.issue': ['rebooking', 'A', ['⚠'], 'Emitir ticket de un grupo'],
  'rebooking.ticket.issue_bulk': ['rebooking', 'A', ['⚠'], 'Emitir tickets en bloque (evento o caso)'],
  'rebooking.external': ['rebooking', 'A', NONE, 'Sincronizar / adoptar reacomodaciones externas'],
  'rebooking.lcc.search': ['rebooking', 'A', NONE, 'Buscar ofertas low-cost'],
  'rebooking.lcc.purchase': ['rebooking', 'A', ['💰', '⚠'], 'Comprar y conciliar pasaje low-cost'],
  // §4.6 Aceptación y comunicación con el pasajero
  'acceptance.force': ['acceptance', 'A', NONE, 'Aceptar en nombre del pasajero (uno; "aceptó en persona")'],
  'acceptance.force_all': ['acceptance', 'A', ['⚠'], 'Aceptar todas (masivo — incl. el "Aceptar todas" de Express)'],
  'notifications.send': ['acceptance', 'A', NONE, 'Reenviar información / llamar al pasajero'],
  // §4.7 Hoteles
  'hotels.allocate': ['hotels', 'A', NONE, 'Asignar hoteles (automático, plan, por PNR)'],
  'hotels.book': ['hotels', 'A', ['💰'], 'Buscar, retener y confirmar habitación'],
  'hotels.policy.override': ['hotels', 'A', ['⚠', '💰'], 'Reservar hotel fuera de política (con motivo; queda registrado)'],
  'hotels.release': ['hotels', 'A', ['⚠'], 'Liberar retención / cancelar reserva confirmada'],
  'hotels.stay': ['hotels', 'A', ['🔒'], 'Noches de estadía y "se queda en casa"'],
  'hotels.rooming.export': ['hotels', 'A', ['🔒'], 'Lista de rooming (nombres completos; imprimir/CSV)'],
  // §4.8 Traslados
  'transport.plan': ['transport', 'A', ['💰'], 'Planificar y despachar (olas, autos por pasajero, agregar vehículo, mover asientos)'],
  'transport.operate': ['transport', 'A', NONE, 'Operar corridas: abordar, no-show, medios propios, cancelar, check-in, recordatorios'],
  'transport.vouchers': ['transport', 'A', ['💰'], 'Vouchers de transporte por PNR (emitir, modificar, cancelar, ver códigos)'],
  'transport.manifest.export': ['transport', 'A', ['🔒'], 'Manifiesto imprimible del traslado'],
  // §4.9 Asistencia (comidas / tarjetas) y excepciones de beneficio
  'care.view': ['care', 'A', NONE, 'Ver asistencia y saldos'],
  'care.issue': ['care', 'A', ['💰'], 'Emitir tarjeta por el monto del derecho (uno o todos)'],
  'care.issue.custom': ['care', 'A', ['💰', '⚠'], 'Emitir por monto discrecional'],
  'care.topup': ['care', 'A', ['💰'], 'Recargar dentro del tope'],
  'care.topup.over_cap': ['care', 'A', ['💰', '⚠'], 'Recargar por encima del tope diario'],
  'care.redeem': ['care', 'A', NONE, 'Marcar consumido / buscar por referencia'],
  'care.void': ['care', 'A', ['💰', '⚠'], 'Anular tarjeta'],
  'care.token.reveal': ['care', 'A', ['💰'], 'Ver código/token de la tarjeta'],
  'benefits.override.propose': ['care', 'A', NONE, 'Proponer excepción de beneficio'],
  'benefits.override.decide': ['care', 'A', ['⚠'], 'Aprobar/rechazar excepción (nunca el autor)'],
  'benefits.override.apply': ['care', 'A', ['💰'], 'Aplicar excepción aprobada'],
  // §4.10 Datos personales (todas las revelaciones se auditan)
  'pii.name.reveal': ['pii', 'A', ['🔒'], 'Revelar nombre'],
  'pii.contact.reveal': ['pii', 'A', ['🔒'], 'Revelar email y teléfono'],
  'pii.document.reveal': ['pii', 'A', ['🔒', '⚠'], 'Revelar documento, pasaporte, fecha de nacimiento'],
  'pii.bulk': ['pii', 'A', ['🔒'], 'Revelar en bloque / imprimir con nombres'],
  'pii.contact.edit': ['pii', 'A', ['🔒'], 'Actualizar contacto del pasajero'],
  'pii.special_needs': ['pii', 'A', ['🔒'], 'Condición especial / adulto responsable (menores)'],
  // §4.11 Costos, reportes y auditoría
  'costs.view': ['reports', 'A', ['💰'], 'Ver costos y tarifas (caso, evento, búsqueda de hotel)'],
  'reconciliation.view': ['reports', 'A', ['💰', '🔒'], 'Conciliación esperado vs ejecutado (+CSV)'],
  'reports.view': ['reports', 'A', ['💰'], 'Reportes y dossier de evento'],
  'reports.export': ['reports', 'A', ['💰'], 'Exportar reportes (CSV)'],
  'activity.view': ['reports', 'A', NONE, 'Actividad por operador'],
  'audit.view': ['reports', 'A', NONE, 'Auditoría y cronología de cambios'],
  'billing.view': ['reports', 'T', ['💰'], 'Facturación'],
  // §4.12 Importación de manifiestos
  'manifests.import': ['manifests', 'A', ['🔒'], 'Subir y preparar manifiestos (editar filas, descartar); confirmar exige además "Declarar disrupción"'],
  // §4.13 Reservas directas
  'reservations.view': ['reservations', 'A', NONE, 'Ver solicitudes, viajes e historial'],
  'reservations.purchase': ['reservations', 'A', ['💰', '⚠'], 'Crear, buscar, comprar y conciliar vuelos y hoteles'],
  'reservations.transport': ['reservations', 'A', ['💰'], 'Viajes y programas de vouchers de transporte'],
  // §4.14 Herramientas
  'counters.qr': ['tools', 'A', NONE, 'Generar QR de mostrador'],
  'manual_review.resolve': ['tools', 'A', NONE, 'Revisión manual: ver y resolver'],
  'provider_sim.answer': ['tools', 'T', NONE, 'Bandeja de proveedor (modo manual)'],
  // §4.15 Configuración
  'policies.view': ['configuration', 'T', NONE, 'Ver políticas y explorador de resolución'],
  'policies.edit': ['configuration', 'T', ['⚠'], 'Crear, editar y generar con IA'],
  'policies.activate': ['configuration', 'T', ['⚠'], 'Activar / desactivar'],
  'contracts.view': ['configuration', 'A', ['💰'], 'Ver contratos y tarifas'],
  'contracts.edit': ['configuration', 'A', ['💰'], 'Crear, editar, extraer de PDF, archivar'],
  'formats.view': ['configuration', 'T', NONE, 'Ver formatos de manifiesto'],
  'formats.edit': ['configuration', 'T', NONE, 'Crear formato / mapear con IA'],
  'formats.approve': ['configuration', 'T', NONE, 'Aprobar / archivar formato'],
  'providers.view': ['configuration', 'T', NONE, 'Estado de proveedores e integraciones'],
  'providers.test': ['configuration', 'T', NONE, 'Probar integración (consume cuota) / refrescar datos de vuelos'],
  'providers.toggle': ['configuration', 'T', ['⚠'], 'Habilitar / deshabilitar integración'],
  // §4.16 Usuarios y accesos
  'users.view': ['users', 'T', NONE, 'Ver usuarios, roles y alcance'],
  'users.invite': ['users', 'T', ['⚠'], 'Invitar usuario'],
  'users.roles.assign': ['users', 'T', ['⚠'], 'Asignar / quitar roles'],
  'users.scope.assign': ['users', 'T', ['⚠'], 'Asignar alcance'],
  'users.suspend': ['users', 'T', ['⚠'], 'Suspender / reactivar'],
  'users.export': ['users', 'T', NONE, 'Exportar matriz usuario × rol × alcance (cumplimiento)'],
  'roles.view': ['users', 'T', NONE, 'Ver roles y sus permisos'],
  'roles.manage': ['users', 'T', ['⚠'], 'Crear, editar y eliminar roles personalizados'],
};

/** The full catalog, in §4 order. Frozen: consumers must never mutate the vocabulary. */
export const PERMISSIONS: readonly IPermissionDef[] = deepFreeze(
  (Object.keys(CATALOG) as PermissionKey[]).map((key): IPermissionDef => {
    const [group, scope, flags, label] = CATALOG[key];
    return {
      key,
      group,
      label,
      labelKey: `permissions.${key}`,
      scope: scope === 'A' ? 'airport' : 'tenant',
      flags: {
        ...(flags.includes('💰') ? { money: true as const } : {}),
        ...(flags.includes('🔒') ? { pii: true as const } : {}),
        ...(flags.includes('⚠') ? { highImpact: true as const } : {}),
      },
    };
  }),
);

/** Every key, in catalog order. */
export const PERMISSION_KEYS: readonly PermissionKey[] = Object.freeze(PERMISSIONS.map((p) => p.key));

const KEY_SET: ReadonlySet<string> = new Set<string>(PERMISSION_KEYS);
const BY_KEY: ReadonlyMap<string, IPermissionDef> = new Map(PERMISSIONS.map((p) => [p.key, p]));

export function isPermissionKey(x: unknown): x is PermissionKey {
  return typeof x === 'string' && KEY_SET.has(x);
}

/** The catalog entry for a key, or `undefined` for anything that is not a v1 key. */
export function permissionDef(key: string): IPermissionDef | undefined {
  return BY_KEY.get(key);
}

/**
 * Does `granted` hold `key`? Exact match only — there is no wildcard and no implication
 * between keys (holding `care.issue.custom` does NOT imply `care.issue`). Fail closed: an
 * unknown `key`, a missing / non-iterable `granted`, or a bare string all answer `false`.
 */
export function hasPermission(
  granted: Iterable<PermissionKey | string> | ReadonlySet<string>,
  key: PermissionKey,
): boolean {
  if (!isPermissionKey(key)) return false;
  if (granted == null || typeof granted === 'string') return false;
  if (granted instanceof Set) return granted.has(key);
  if (typeof (granted as Iterable<string>)[Symbol.iterator] !== 'function') return false;
  for (const g of granted as Iterable<string>) {
    if (g === key) return true;
  }
  return false;
}

/**
 * The effective permission set of a user = the union of their roles' permissions,
 * de-duplicated and returned in catalog order. Keys that are not in the v1 catalog (a
 * stale custom role, a typo, a key from a newer release) are dropped — fail closed.
 */
export function effectivePermissions(
  roles: readonly { permissions: readonly string[] }[],
): PermissionKey[] {
  const granted = new Set<string>();
  for (const role of Array.isArray(roles) ? roles : []) {
    const permissions: unknown = role?.permissions;
    if (!Array.isArray(permissions)) continue;
    for (const p of permissions) {
      if (typeof p === 'string') granted.add(p);
    }
  }
  return PERMISSION_KEYS.filter((k) => granted.has(k));
}
